import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SortDetails } from 'shared/page/SortDetails';
import { useUnleash } from '@/hooks/unleash';
import type { NumberRange, PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import { ActivityFilter } from './filters/checkbox/ActivityFilter';
import { CharterSupplierFilter } from './filters/checkbox/CharterSupplierFilter';
import { DestinationFilter } from './filters/checkbox/DestinationFilter';
import { FacilityFilter } from './filters/checkbox/FacilityFilter';
import { HotelRatingFilter } from './filters/checkbox/HotelRatingFilter';
import { MealFilter } from './filters/checkbox/MealFilter';
import { NonCharterSupplierFilter } from './filters/checkbox/NonCharterSupplierFilter';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import { FilterGroup } from './filters/FilterGroup';
import { KeywordFilter } from './filters/KeywordFilter';
import ResultOriginDropdown from './filters/origin/ResultOriginDropdown';
import { PriceRangeFilter } from './filters/PriceRangeFilter';
import { ResultPagePaxCombination } from './filters/paxCombination/ResultPagePaxCombination';
import DistanceToBeachSlider from './filters/slider/DistanceToBeachSlider';
import DistanceToCitySlider from './filters/slider/DistanceToCitySlider';
import ResultTravelTimePicker from './filters/travelTime/ResultTravelTimePicker';
import { ResultList } from './resultList/ResultList';
import DesktopTopBar from './topBar/DesktopTopBar';
import MobileTopBar from './topBar/MobileTopBar';

export const ResultPageLayout = () => {
    const filtersOpen = useSelector((s: PaleState) => s.appState.filtersOpen);
    const priceRange = useSelector((s: PaleState) => s.filters.priceRange);
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const unleash = useUnleash();

    const updatePriceRange = (values: NumberRange) => {
        dispatch((inner) => {
            inner(actions.filterPriceRange(values));
            return inner(fetchResults());
        });
    };

    return (
        <>
            <SimpleHead
                title="Pakkereiser - sammenlign pris | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/resultat"
                description="Finn billige pakkereiser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <MobileTopBar />
                <h1 className="sr-only">Pakkereiser</h1>
                <DesktopTopBar />
                <div className="md:grid md:grid-cols-3 md:gap-32 pb-32 ">
                    <div className={cn('divide-y-1', { 'hidden md:block': !filtersOpen })}>
                        <div test-id="origin" className="px-8 py-16">
                            <h3 className="h4 mb-16">Utreise og dato</h3>
                            <ResultOriginDropdown />
                            <h3 className="sr-only">Alternativer for avreise og hjemreise</h3>
                            <ResultTravelTimePicker />
                            {unleash?.numPax && <ResultPagePaxCombination />}
                        </div>
                        <FilterGroup title="Reisemål" testId="destination">
                            <DestinationFilter />
                        </FilterGroup>
                        <FilterGroup title="Pris per person" testId="pricePerPerson">
                            <PriceRangeFilter
                                id="price"
                                legend="Pris per person"
                                labels={['Fra kroner', 'Til kroner']}
                                affix="kr"
                                range={priceRange}
                                updateRange={updatePriceRange}
                            />
                        </FilterGroup>
                        <FilterGroup title="Hotellstandard" testId="hotelStandard">
                            <HotelRatingFilter />
                        </FilterGroup>
                        <FilterGroup title="Avstander" testId="distances" className="space-y-16">
                            <DistanceToBeachSlider />
                            <DistanceToCitySlider />
                        </FilterGroup>
                        <FilterGroup title="Fasiliteter og tilbud" testId="facilitiesAndOffers" className="space-y-16">
                            <MealFilter />
                            <FacilityFilter />
                            <ActivityFilter />
                        </FilterGroup>
                        <FilterGroup title="Søk etter..." testId="keywords">
                            <KeywordFilter />
                        </FilterGroup>
                        <FilterGroup title="Tilbydere og selskaper" testId="providers" className="space-y-16">
                            <CharterSupplierFilter />
                            <NonCharterSupplierFilter />
                        </FilterGroup>
                    </div>
                    <section className={cn('md:col-span-2', { 'hidden md:block': filtersOpen })}>
                        <SortDetails section="pakkereiser" className="md:hidden py-16" />
                        <CurrentFilters />
                        <ResultList paxEnabled={unleash?.numPax} />
                    </section>
                </div>
            </Main>
        </>
    );
};
